/**
 * The line under the person picker's list (8 Oct).
 *
 * WHY: on LIVE, "daniel" matched twelve people and the server returned all
 * twelve, but the popup was 20rem tall with two-line rows and a hairline
 * scrollbar, so it looked like five Daniels and nothing more. The list is now
 * taller with a visible scrollbar, and this footer says how many there are:
 *
 * - `truncated`: the server cut the list. "Showing 20 of 37 — keep typing to
 *   narrow". Falls back to the plain "keep typing" line when the server did
 *   not send a total.
 * - `scroll`: everything is here but more than fits on screen at once, so the
 *   footer says how many, and that the list scrolls.
 * - `close`: nobody matched as typed and every row is a close spelling
 *   (16 Oct, LIVE: "Daheem Rogers" is Daheem Rodgers), so the footer says
 *   "no exact match - did you mean one of these?" rather than letting a
 *   near-miss pass for the person typed.
 * - null: nothing worth saying.
 *
 * Pure, so the node tests can pin it without a DOM.
 */

/** Rows visible without scrolling (two-line rows in the taller popup). */
export const PICKER_VISIBLE_ROWS = 8;

export type PickerFooter =
  | { kind: "truncated"; shown: number; total: number | null }
  | { kind: "scroll"; count: number }
  | { kind: "close" }
  | null;

export interface PickerResult {
  items?: readonly { close_match?: boolean | null }[] | null;
  truncated?: boolean | null;
  total?: number | null;
}

export function pickerFooter(
  result: PickerResult | null | undefined,
  visibleRows: number = PICKER_VISIBLE_ROWS,
): PickerFooter {
  const shown = result?.items?.length ?? 0;
  if (shown === 0) {
    return null;
  }
  if (result?.items?.every((item) => item.close_match === true) === true) {
    return { kind: "close" };
  }
  if (result?.truncated === true) {
    const total = typeof result.total === "number" && result.total > shown ? result.total : null;
    return { kind: "truncated", shown, total };
  }
  if (shown > visibleRows) {
    return { kind: "scroll", count: shown };
  }
  return null;
}
